<template>
  <UModal
    v-model:open="isOpen"
    title="Workflow beenden"
    description="Außerordentliche Beendigung für blockierte oder fehlerhaft zugewiesene Workflows."
  >
    <template #body>
      <div class="space-y-4 py-2">
        <!-- Information box explaining admin permission and consequences -->
        <div
          class="p-3.5 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 text-xs space-y-2"
        >
          <div class="flex items-center gap-2 font-bold text-amber-950 dark:text-amber-100 text-sm">
            <UIcon
              name="i-heroicons-shield-exclamation"
              class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0"
            />
            <span>Administrativer Ausnahme-Eingriff (Tenant Admin)</span>
          </div>
          <p>
            Dies ist kein regulärer Workflow-Schritt, sondern eine Maßnahme für Ausnahmefälle (z.&nbsp;B. blockierte Vorgänge durch falsche Gruppenzuweisungen, Konfigurationskonflikte oder nicht besetzte Gruppen).
          </p>
          <ul class="list-disc list-inside space-y-1 text-amber-900/90 dark:text-amber-200/90">
            <li>
              <strong>ERP-Freigabe:</strong> Der Beleg wird in microtech ERP sofort aus der externen Bearbeitung entsperrt und die Workflow-Felder werden zurückgesetzt.
            </li>
            <li>
              <strong>Dauerhafter Abbruch:</strong> Die Instanz wird unwiderruflich beendet und die Aktion revisionssicher mit Ihrer Begründung im Protokoll hinterlegt.
            </li>
          </ul>
        </div>

        <div
          v-if="task"
          class="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 space-y-1 text-sm border border-default"
        >
          <div><span class="font-bold">Objektnummer:</span> {{ task.objectNumber || '-' }}</div>
          <div>
            <span class="font-bold">Workflow:</span>
            {{ task.blueprint?.id || task.blueprintId || '-' }}
          </div>
          <div v-if="task.currentStep">
            <span class="font-bold">Aktueller Schritt:</span>
            {{ currentStepName }}
          </div>
        </div>
        <div class="space-y-1.5">
          <label class="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Begründung (optional)
          </label>
          <UTextarea
            v-model="internalReason"
            placeholder="Grund für das Beenden des Workflows angeben..."
            :rows="3"
            class="w-full"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <UButton
          color="neutral"
          variant="ghost"
          label="Abbrechen"
          :disabled="loading"
          @click="isOpen = false"
        />
        <UButton
          color="error"
          label="Workflow beenden"
          :loading="loading"
          @click="onConfirm"
        />
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

export interface CancelTaskTarget {
  guid?: string
  objectNumber?: string
  blueprint?: { id?: string; [key: string]: any } // eslint-disable-line @typescript-eslint/no-explicit-any
  blueprintId?: string
  currentStep?: { name?: string; [key: string]: any } | string | null // eslint-disable-line @typescript-eslint/no-explicit-any
}

const props = withDefaults(
  defineProps<{
    open?: boolean
    task?: CancelTaskTarget | null
    loading?: boolean
    reason?: string
  }>(),
  {
    open: false,
    task: null,
    loading: false,
    reason: '',
  }
)

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'update:reason', value: string): void
  (e: 'confirm', reason: string): void
}>()

const isOpen = computed({
  get: () => props.open,
  set: (val: boolean) => emit('update:open', val),
})

const localReason = ref(props.reason)
watch(
  () => props.reason,
  (val) => {
    localReason.value = val
  }
)

const internalReason = computed({
  get: () => localReason.value,
  set: (val: string) => {
    localReason.value = val
    emit('update:reason', val)
  },
})

const currentStepName = computed(() => {
  if (!props.task?.currentStep) return ''
  if (typeof props.task.currentStep === 'string') return props.task.currentStep
  return props.task.currentStep.name || ''
})

const onConfirm = () => {
  emit('confirm', internalReason.value)
}
</script>
